import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  activarCategoriaHttp,
  createCategoriaHttp,
  desactivarCategoriaHttp,
  editarCategoriaHttp,
  getCategoriasHttp,
} from "actions/categorias";

export const useCategorias = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  /* CREAR CATEGORÍA */
  const [procesandoCategoria, setProcesandoCategoria] = useState<boolean>(false);
  const [isAlertOpenCrearCategoria, setIsAlertOpenCrearCategoria] = useState(false);
  const handleisAlertCrearCategoriaOpen = () => setIsAlertOpenCrearCategoria(true);
  const handleisAlertCrearCategoriaClose = () => setIsAlertOpenCrearCategoria(false);

  const [verEditarCategoria, setVerEditarCategoria] = useState<any>(null);
  const [isAlertOpenEditarCategoria, setIsAlertOpenEditarCategoria] = useState(false);
  const handleisAlertOpenEditarCategoria = () => setIsAlertOpenEditarCategoria(true);
  const handleisAlertCloseEditarCategoria = () => setIsAlertOpenEditarCategoria(false);

  const [categorias, setCategorias] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //para buscador
  const [buscador, setBuscador] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  //Debounce para evitar muchas peticiones
  const handleBuscadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscador(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      getCategorias({ search: value });
    }, 500);
    setDebounceTimeout(timeout);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      nombre: "",
      descripcion: "",
    },
    validationSchema: Yup.object({
      clave: Yup.string(),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      descripcion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
    }),
    onSubmit: async (values) => {
      //console.log("Formulario enviado:", values);
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getCategorias = useCallback(
    async (params?: { search?: string; fecha1?: string; fecha2?: string }) => {
      try {
        setProcesando(true);
        const categorias = await getCategoriasHttp(params);
        setCategorias(categorias);
        setProcesando(false);
      } catch (error) {
        setProcesando(false);
        const message = getErrorHttpMessage(error);
        setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
        handleisAlertOpen();
      }
    },
    []
  );

  const createCategoria = async (datos: any) => {
    try {
      setProcesandoCategoria(true);
      const response: any = await createCategoriaHttp(datos);
      setCategorias((prevCategorias: any[]) => [{ ...response, productos: 0 }, ...prevCategorias]);

      setMensajeAlert(intl.formatMessage({ id: "categoria_añadida_correctamente" }));
      formik.resetForm();
      handleisAlertCrearCategoriaClose();
      handleisAlertOpen();
      setProcesandoCategoria(false);
    } catch (error) {
      setProcesandoCategoria(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "error_añadir_categoria" }));
      handleisAlertOpen();
    }
  };
  const editarCategoria = async (datos: any) => {
    try {
      setProcesandoCategoria(true);
      const response: any = await editarCategoriaHttp(datos);

      setCategorias((prevCategorias: any[]) =>
        prevCategorias.map((c) =>
          c.id === response?.id
            ? {
                ...c,
                clave: response?.clave,
                nombre: response?.nombre,
                descripcion: response?.descripcion,
              }
            : c
        )
      );
      setMensajeAlert(intl.formatMessage({ id: "categoria_editada_correctamente" }));
      formik.resetForm();
      handleisAlertCloseEditarCategoria();
      handleisAlertOpen();
      setProcesandoCategoria(false);
    } catch (error) {
      setProcesandoCategoria(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "categoria_editada_error" }));
      handleisAlertOpen();
    }
  };

  const desactivarCategoria = async (datos: any) => {
    try {
      setProcesando(true);
      const categoria: any = await desactivarCategoriaHttp(datos);
      setCategorias((prevCategorias: any[]) => {
        return prevCategorias.map((c) => {
          if (c.id === categoria.id) {
            return {
              ...c,
              estatus: categoria.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "categoria_desactivada_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "categoria_desactivada_error" }));
      handleisAlertOpen();
    }
  };
  const activarCategoria = async (datos: any) => {
    try {
      setProcesando(true);
      const categoria: any = await activarCategoriaHttp(datos);
      setCategorias((prevCategorias: any[]) => {
        return prevCategorias.map((c) => {
          if (c.id === categoria.id) {
            return {
              ...c,
              estatus: categoria.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "categoria_activada_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "categoria_activada_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarCategoria) {
      formik.setValues({
        clave: verEditarCategoria?.clave || "",
        nombre: verEditarCategoria?.nombre || "",
        descripcion: verEditarCategoria?.descripcion || "",
      });
    }
  }, [verEditarCategoria]);

  useEffect(() => {
    getCategorias();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const categoriasPaginadas = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return categorias?.slice(startIndex, endIndex) || [];
  }, [categorias, page, rowsPerPage]);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    categorias,
    //crear categoria
    isAlertOpenCrearCategoria,
    handleisAlertCrearCategoriaOpen,
    handleisAlertCrearCategoriaClose,
    getFieldColor,
    procesandoCategoria,
    createCategoria,
    desactivarCategoria,
    activarCategoria,
    editarCategoria,
    verEditarCategoria,
    isAlertOpenEditarCategoria,
    handleisAlertOpenEditarCategoria,
    handleisAlertCloseEditarCategoria,
    setVerEditarCategoria,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    buscador,
    handleBuscadorChange,
    categoriasPaginadas,
  };
};
